/**
 * <SettingsSection> — un groupe de réglages (molecule).
 *
 * Le titre de section, puis ses lignes (`<SettingRow>`) dans une carte, séparées par un filet :
 * « Gestion » et « Facturation » du portail d'abonnement, les sections du compte de l'élève.
 * Née dans `<SubscriptionPortalTemplate>`, sortie telle quelle pour être partagée.
 *
 * Une carte qui porte autre chose que des lignes sous le même intitulé (des statistiques, des
 * bascules, un texte) passe son contenu en `children` : l'intitulé ne se compose qu'ici.
 *
 * Une section sans ligne ni contenu ne se rend pas : une ligne n'existe que si son geste existe,
 * et c'est l'appelant qui la retire de la liste.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Divider } from "../../atoms/Divider"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Card } from "../Card"
import { SettingRow, type SettingRowProps } from "../SettingRow"

export type SettingsSectionRow = SettingRowProps & { key: string }

export type SettingsSectionProps = {
    /** Intitulé de la section. Absent : la carte commence à sa première ligne. */
    title?: string
    /** Les lignes de la section. Aucune par défaut. */
    rows?: SettingsSectionRow[]
    /** Un contenu libre sous l'intitulé, après les lignes. */
    children?: React.ReactNode
    testID?: string
}

export function SettingsSection(props: SettingsSectionProps) {
    const { title, rows = [], children, testID } = props

    if (rows.length === 0 && children == null) return null

    return (
        <Card p="4" testID={testID}>
            {/* Même style de libellé que les sections de l'accueil : il ne s'écrit qu'une fois. */}
            {title != null && <SectionLabel style={settingsSectionStyles.title}>{title}</SectionLabel>}
            {rows.map(({ key, ...row }, index) => (
                <React.Fragment key={key}>
                    {index > 0 && <Divider spacing="1" />}
                    <SettingRow {...row} />
                </React.Fragment>
            ))}
            {children}
        </Card>
    )
}

/** L'intitulé d'une carte de réglages, partagé avec son squelette : il tombe au même endroit. */
export const settingsSectionStyles = StyleSheet.create((theme) => ({
    title: { letterSpacing: 0.4, marginBottom: theme.spacing["2"] },
}))
